import React, { useState, useEffect } from 'react';
import styles from './Admin.module.css';
import UserDetail from './UserDetail';

const UserList = ({ users, updateUserVerification }) => {
    const [searchTerm, setSearchTerm] = useState('');
    const [filter, setFilter] = useState('all'); // 'all', 'verified', 'unverified', 'pending'
    const [selectedUser, setSelectedUser] = useState(null);
    const [localUsers, setLocalUsers] = useState([]);
    const [selectedCountry, setSelectedCountry] = useState('all');
    const [selectedProfession, setSelectedProfession] = useState('all');

    // Synchroniser les utilisateurs locaux avec les props
    useEffect(() => {
        setLocalUsers(users);
    }, [users]);

    // Extraire la liste unique des pays
    const countryList = Array.from(
        new Set(localUsers.filter(user => user.country_name).map(user => user.country_name))
    ).sort();

    // Extraire la liste unique des professions
    const professionList = Array.from(
        new Set(localUsers.filter(user => user.profession_name).map(user => user.profession_name))
    ).sort();

    // Filtrer les utilisateurs selon les critères
    const filteredUsers = localUsers.filter(user => {
        // Log pour débogage
        console.log(`User ${user.id} (${user.firstname} ${user.lastname}): is_verified = ${JSON.stringify(user.is_verified)}`);

        // Filtre de recherche
        const searchMatch =
            user.firstname?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            user.lastname?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            user.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            user.working_place?.toLowerCase().includes(searchTerm.toLowerCase());

        // Filtre de statut
        let statusMatch = true;
        if (filter === 'verified') {
            statusMatch = user.is_verified === true;
        } else if (filter === 'unverified') {
            statusMatch = user.is_verified === false;
        } else if (filter === 'pending') {
            statusMatch = user.is_verified === null;
        }

        // Filtre de pays
        let countryMatch = true;
        if (selectedCountry !== 'all') {
            countryMatch = user.country_name === selectedCountry;
        }

        // Filtre de profession
        let professionMatch = true;
        if (selectedProfession !== 'all') {
            professionMatch = user.profession_name === selectedProfession;
        }

        return searchMatch && statusMatch && countryMatch && professionMatch;
    });

    const handleRowClick = (user) => {
        setSelectedUser(user);
    };

    const handleCloseDetail = () => {
        setSelectedUser(null);
    };

    const handleUpdateVerification = (userId, isVerified) => {
        // Mettre à jour localement avant l'appel API
        setLocalUsers(prevUsers =>
            prevUsers.map(user =>
                user.id === userId
                    ? { ...user, is_verified: isVerified }
                    : user
            )
        );

        // Appeler la fonction de mise à jour du parent
        updateUserVerification(userId, isVerified);
    };

    // Fonction pour déterminer le statut d'affichage
    const getDisplayStatus = (status) => {
        if (status === true) return 'Vérifié';
        if (status === false) return 'Non vérifié';
        return 'En attente';
    };

    // Fonction pour exporter les utilisateurs filtrés en CSV
    const exportToCSV = () => {
        // Définir les en-têtes du CSV
        const headers = [
            'ID',
            'Nom',
            'Prénom',
            'Email',
            'Profession',
            'Lieu de travail',
            'Pays',
            'Statut',
            'Date d\'inscription'
        ];

        // Transformer les données des utilisateurs en lignes CSV
        const csvRows = filteredUsers.map(user => [
            user.id,
            user.lastname || '',
            user.firstname || '',
            user.email || '',
            user.profession_name || user.profession_id || '',
            user.working_place || '',
            user.country_name || user.country_id || '',
            getDisplayStatus(user.is_verified),
            user.registration_date ? new Date(user.registration_date).toLocaleDateString() : ''
        ]);

        // Ajouter les en-têtes au début
        csvRows.unshift(headers);

        // Convertir chaque ligne en chaîne CSV et les joindre avec des sauts de ligne
        const csvContent = csvRows
            .map(row =>
                row
                    .map(cell =>
                        // Échapper les guillemets et entourer de guillemets si nécessaire
                        typeof cell === 'string' && (cell.includes(',') || cell.includes('"') || cell.includes('\n'))
                            ? `"${cell.replace(/"/g, '""')}"`
                            : cell
                    )
                    .join(',')
            )
            .join('\n');

        // Créer un Blob avec le contenu CSV
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });

        // Créer un URL pour le Blob
        const url = URL.createObjectURL(blob);

        // Créer un lien pour télécharger le fichier
        const link = document.createElement('a');
        link.setAttribute('href', url);

        // Définir le nom du fichier avec la date actuelle
        const date = new Date().toISOString().split('T')[0];
        link.setAttribute('download', `utilisateurs_sav-iol_${date}.csv`);

        // Ajouter le lien au document, cliquer dessus, puis le supprimer
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    return (
        <div className={styles.userListContainer}>
            <div className={styles.filterControls}>
                <div className={styles.searchBox}>
                    <input
                        type="text"
                        placeholder="Rechercher un utilisateur..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className={styles.searchInput}
                    />
                </div>

                <div className={styles.filterButtons}>
                    <button
                        className={`${styles.filterButton} ${filter === 'all' ? styles.active : ''}`}
                        onClick={() => setFilter('all')}
                    >
                        Tous
                    </button>
                    <button
                        className={`${styles.filterButton} ${filter === 'verified' ? styles.active : ''}`}
                        onClick={() => setFilter('verified')}
                    >
                        Vérifiés
                    </button>
                    <button
                        className={`${styles.filterButton} ${filter === 'unverified' ? styles.active : ''}`}
                        onClick={() => setFilter('unverified')}
                    >
                        Non vérifiés
                    </button>
                    <button
                        className={`${styles.filterButton} ${filter === 'pending' ? styles.active : ''}`}
                        onClick={() => setFilter('pending')}
                    >
                        En attente
                    </button>
                </div>

                <div className={styles.advancedFilters}>
                    <div className={styles.filterSelect}>
                        <label>Pays:</label>
                        <select
                            value={selectedCountry}
                            onChange={(e) => setSelectedCountry(e.target.value)}
                            className={styles.filterSelectInput}
                        >
                            <option value="all">Tous les pays</option>
                            {countryList.map(country => (
                                <option key={country} value={country}>
                                    {country}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div className={styles.filterSelect}>
                        <label>Profession:</label>
                        <select
                            value={selectedProfession}
                            onChange={(e) => setSelectedProfession(e.target.value)}
                            className={styles.filterSelectInput}
                        >
                            <option value="all">Toutes les professions</option>
                            {professionList.map(profession => (
                                <option key={profession} value={profession}>
                                    {profession}
                                </option>
                            ))}
                        </select>
                    </div>
                </div>
            </div>

            <div className={styles.listActions}>
                <div className={styles.userCount}>
                    {filteredUsers.length} utilisateur(s) trouvé(s)
                </div>
                <button
                    className={styles.exportButton}
                    onClick={exportToCSV}
                    disabled={filteredUsers.length === 0}
                >
                    Exporter en CSV
                </button>
            </div>

            <div className={styles.tableContainer}>
                <table className={styles.userTable}>
                    <thead>
                        <tr>
                            <th>Nom</th>
                            <th>Prénom</th>
                            <th>Email</th>
                            <th>Profession</th>
                            <th>Lieu de travail</th>
                            <th>Pays</th>
                            <th>Statut</th>
                            <th>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {filteredUsers.length > 0 ? (
                            filteredUsers.map(user => (
                                <tr
                                    key={user.id}
                                    className={
                                        user.is_verified === true
                                            ? styles.verifiedUser
                                            : user.is_verified === false
                                                ? styles.unverifiedUser
                                                : styles.pendingUser
                                    }
                                    onClick={() => handleRowClick(user)}
                                >
                                    <td>{user.lastname || '-'}</td>
                                    <td>{user.firstname || '-'}</td>
                                    <td>{user.email || '-'}</td>
                                    <td>{user.profession_name || user.profession_id || '-'}</td>
                                    <td>{user.working_place || '-'}</td>
                                    <td>{user.country_name || user.country_id || '-'}</td>
                                    <td>
                                        {getDisplayStatus(user.is_verified)}
                                    </td>
                                    <td className={styles.actionButtons}>
                                        <button
                                            className={`${styles.verifyButton} ${user.is_verified === true ? styles.active : ''}`}
                                            onClick={(e) => {
                                                e.stopPropagation(); // Empêcher le clic sur la ligne
                                                handleUpdateVerification(user.id, true);
                                            }}
                                            disabled={user.is_verified === true}
                                        >
                                            Approuver
                                        </button>
                                        <button
                                            className={`${styles.rejectButton} ${user.is_verified === false ? styles.active : ''}`}
                                            onClick={(e) => {
                                                e.stopPropagation(); // Empêcher le clic sur la ligne
                                                handleUpdateVerification(user.id, false);
                                            }}
                                            disabled={user.is_verified === false}
                                        >
                                            Rejeter
                                        </button>
                                    </td>
                                </tr>
                            ))
                        ) : (
                            <tr>
                                <td colSpan="8" className={styles.noResults}>
                                    Aucun utilisateur trouvé
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            {selectedUser && (
                <UserDetail
                    user={selectedUser}
                    onClose={handleCloseDetail}
                    updateUserVerification={handleUpdateVerification}
                />
            )}
        </div>
    );
};

export default UserList; 